import { useState, useEffect } from 'react'
import { X, Check, Search, Plus, Layers, Scan } from 'lucide-react'
import { useI18n } from '../i18n'
import { profileAPI, insuranceRegistryAPI } from '../services/api'
import EligibilityBadge from './EligibilityBadge'
import CardScanner from './CardScanner'
import DobPicker from './DobPicker'
import InsuranceCandidateList from './InsuranceCandidateList'
import useCardScanPair from '../hooks/useCardScanPair'
import useResolvedCardUrls from '../hooks/useResolvedCardUrls'
import CardThumb from './CardThumb' // s110 b77
import { cardInlineSrc } from '../utils/cardRef'
import { processCardFile, persistCardData } from '../utils/processCardImage'
import { cardUploadFailureMessage } from '../utils/uploadRefusal'
import { secondaryFromResponse } from '../utils/insuranceDiscovery'
import { secondarySaveReuseOf, secondaryReuseParams } from '../utils/eligibilityLimit'
import { DISCOVERY_CAP, needsPolicyholder, policyholderComplete, discoveryOutcome, outcomeMessageKey } from '../utils/discoverySearch'
import { INSURANCE_DISCOVERY_ENABLED } from '../config/featureFlags'
import { secondaryPayload, secondaryPolicyholderBody, secondaryFromScan } from '../utils/secondaryInsurance'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

/**
 * SecondaryInsuranceCard — v238.9.0 (S17-4, Coordination of Benefits), i18n v238.11.0
 *
 * Lets a patient register (or clear) a SECONDARY insurance policy. On save the
 * server stamps the Stedi payer ID and fires a silent secondary eligibility
 * check; the parsed result renders in the existing EligibilityBadge so the
 * patient sees their secondary benefits in the same "full benefits" surface.
 *
 * ★ U2-INS C4 (TEST 3.07): the secondary gets what the primary has —
 *   * a card scan, front and back (the same CardScanner, useCardScanPair('insurance') and
 *     persistCardData as the primary; the images are kept as secondary.cardFront / cardBack
 *     storage references, never base64). TEST 1.01's back-of-card payer rule is inherited as it is;
 *   * the policyholder (relationship, first name, last name, date of birth), the
 *     profileInsurance2.* keys, no new copy;
 *   * "Search for this insurance": discovery with target 'secondary', its own 3 searches. The
 *     server searches with the SAVED secondary, so the search first saves it: through the
 *     secondary route when there is a member ID, otherwise only the policyholder, through PUT
 *     /profile (the route's no-member-ID path would CLEAR the secondary; utils/secondaryInsurance.js).
 *   Every request of this card runs in the screen's one queue (`runExclusive`, Profile →
 *   Insurance C5): the secondary route and its eligibility check write the whole insurance
 *   column as they read it before a Stedi call, so a screen save inside that window is lost.
 *
 * Brand-locked colors only. No purple, no amber.
 *
 * Props:
 *   userId               — patient user id
 *   secondary            — profile.insurance.secondary (or null)
 *   secondaryEligibility — profile.insurance.last_eligibility_secondary (or null)
 *   secondaryDiscovery   — profile.insurance.secondary_discovery_result (or null)
 *   onSaved              — (result:{secondary, eligibility}) => void   updates the store
 *   onPolicyholderSaved  — (secondary) => void   the policyholder saved without a member ID
 *   onSecondaryDiscovery — (result) => void   the server's secondary_discovery_result, for the store
 *   runExclusive         — (task) => Promise   the screen's request queue
 */
const NAVY = '#15354F'
const BLUE = '#46A8F9'
const SLATE = '#94A3B8'

export default function SecondaryInsuranceCard({ userId, secondary, secondaryEligibility, secondaryDiscovery, onSaved, onPolicyholderSaved, onSecondaryDiscovery, runExclusive }) {
  const { t, language } = useI18n()
  const queued = typeof runExclusive === 'function' ? runExclusive : (task) => task()
  const hasSecondary = !!(secondary && (secondary.memberId || secondary.member_id))
  const [editing, setEditing] = useState(false)
  const [carrier, setCarrier] = useState({ name: secondary?.provider || '', payerId: secondary?.payerId || '' })
  const [memberId, setMemberId] = useState(secondary?.memberId || '')
  const [groupNumber, setGroupNumber] = useState(secondary?.groupNumber || '')
  const [policyholder, setPolicyholder] = useState({
    subscriberRelationship: secondary?.subscriberRelationship || 'Self',
    subscriberFirstName: secondary?.subscriberFirstName || '',
    subscriberLastName: secondary?.subscriberLastName || '',
    subscriberDOB: secondary?.subscriberDOB || '',
  })
  const [cards, setCards] = useState({ cardFront: secondary?.cardFront || null, cardBack: secondary?.cardBack || null })
  const [scannerOpen, setScannerOpen] = useState(null) // null | 'cardFront' | 'cardBack'
  const [cardUploads, setCardUploads] = useState(0)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [saving, setSaving] = useState(false)
  // PROD-1133: the last save reused the stored check ({ checkedAt, nextCheckAt } from the server) or not (null).
  const [reuse, setReuse] = useState(null)
  const [searchStatus, setSearchStatus] = useState('idle')
  const [searchesLeft, setSearchesLeft] = useState(Number.isInteger(secondaryDiscovery?.searchesLeft) ? secondaryDiscovery.searchesLeft : null)
  const [candidates, setCandidates] = useState([])
  const cardUrls = useResolvedCardUrls(cards)

  // Debounced Stedi payer typeahead (same source as the primary carrier picker).
  useEffect(() => {
    const q = query.trim()
    if (q.length < 2) { setResults([]); setSearching(false); return }
    setSearching(true)
    const tid = setTimeout(async () => {
      const res = await insuranceRegistryAPI.searchPayers(q)
      setResults(Array.isArray(res) ? res : [])
      setSearching(false)
    }, 350)
    return () => clearTimeout(tid)
  }, [query])

  const pickCarrier = (p) => {
    setCarrier({ name: p.displayName || p.name || '', payerId: p.stediId || p.primaryPayerId || '' })
    setQuery(''); setResults([])
  }

  // ★ C4: the scan. Auto-fill waits for BOTH sides (the back carries the printed payer ID).
  const cardPair = useCardScanPair('insurance', (merged) => {
    const next = secondaryFromScan(merged, { carrier, memberId, groupNumber })
    setCarrier(next.carrier); setMemberId(next.memberId); setGroupNumber(next.groupNumber)
  })
  const handleCardCapture = (side, cardData, scanToken) => {
    // PROD-1189b: a result whose side was cleared or replaced since its extract started is dropped.
    if (scanToken && !cardPair.isCurrent(scanToken)) return
    const token = scanToken || cardPair.beginSide(side)
    setCards((prev) => ({ ...prev, [side]: cardData }))
    cardPair.captureSide(side, cardData, token)
    setScannerOpen(null)
    setCardUploads((n) => n + 1)
    persistCardData(cardData, 'Insurance', side === 'cardFront' ? 'front' : 'back').then((enriched) => {
      if (!cardPair.isCurrent(token)) return // PROD-1189b: removed or replaced meanwhile
      if (enriched && enriched.storagePath) {
        setCards((prev) => ({ ...prev, [side]: enriched }))
        cardPair.captureSide(side, enriched, token)
      } else if (enriched && enriched.uploadError) {
        alert(cardUploadFailureMessage(t, enriched.uploadError))
      }
    }).catch(() => {}).finally(() => setCardUploads((n) => Math.max(0, n - 1)))
  }

  const canSave = !!(carrier.name && memberId.trim()) && cardUploads === 0
  const payloadNow = () => secondaryPayload({ carrier, memberId, groupNumber, policyholder, cardFront: cards.cardFront, cardBack: cards.cardBack })

  const save = async () => {
    if (!canSave || saving || !userId) return
    const payload = payloadNow()
    if (!payload) return // ★ INSURE-2: never post a secondary without a member ID (the server clears it)
    setSaving(true)
    try {
      const result = await queued(() => profileAPI.saveSecondaryInsurance(userId, payload))
      setReuse(secondarySaveReuseOf(result))
      if (onSaved) onSaved(result || { secondary: payload, eligibility: null })
      setEditing(false)
    } catch (e) {
      console.warn('[COB] save secondary failed:', e?.message || e)
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    if (saving || !userId) return
    setSaving(true)
    try {
      await queued(() => profileAPI.saveSecondaryInsurance(userId, null))
      setReuse(null)
      if (onSaved) onSaved({ secondary: null, eligibility: null })
      setCarrier({ name: '', payerId: '' }); setMemberId(''); setGroupNumber('')
      setCards({ cardFront: null, cardBack: null })
      setEditing(false)
    } catch (e) {
      console.warn('[COB] remove secondary failed:', e?.message || e)
    } finally {
      setSaving(false)
    }
  }

  // ★ C4 (TEST 3.07): search for the secondary, in ONE queued task: the save the server will
  //   read, then the search. A save that did not land sends no search.
  const searchBlocked = searchStatus === 'searching' || saving || cardUploads > 0 || searchesLeft === 0
    || (needsPolicyholder(policyholder.subscriberRelationship) && !policyholderComplete(policyholder))
  const handleSearch = async () => {
    if (!userId || searchBlocked) return
    setSearchStatus('searching'); setCandidates([])
    try {
      const out = await queued(async () => {
        const payload = payloadNow()
        if (payload) {
          const res = await profileAPI.saveSecondaryInsurance(userId, payload)
          const echo = secondaryFromResponse(res, payload)
          if (!echo.ok) return { saved: false }
          setReuse(secondarySaveReuseOf(res))
          if (onSaved) onSaved(res)
        } else {
          const body = secondaryPolicyholderBody(secondary, policyholder)
          await profileAPI.update({ insurance: body })
          if (onPolicyholderSaved) onPolicyholderSaved(body.secondary)
        }
        return { saved: true, resp: await profileAPI.runInsuranceDiscovery(userId, { target: 'secondary', reason: 'search' }) }
      })
      if (!out.saved) { setSearchStatus('failed'); return }
      const o = discoveryOutcome(out.resp)
      if (o.searchesLeft !== null) setSearchesLeft(o.searchesLeft)
      if (o.discovery && onSecondaryDiscovery) onSecondaryDiscovery(o.discovery)
      if (o.kind === 'found') setCandidates(o.discovery.candidates)
      setSearchStatus(o.kind)
    } catch (e) {
      setSearchStatus('failed')
    }
  }
  // "Use this plan" fills the editor; "Save secondary" saves it as today.
  const applyCandidate = async (idx) => {
    const c = candidates[idx]
    if (!c) return { success: false }
    setCarrier({ name: c.payerName || '', payerId: c.payerId || '' })
    setMemberId(c.memberIdGuess || '')
    if (c.groupNumber) setGroupNumber(c.groupNumber)
    return { success: true }
  }

  const inputCls = "w-full px-3.5 py-2.5 border-[1.5px] border-gray-200 rounded-[14px] text-[13px] focus:outline-none focus:border-[#46A8F9] transition-colors"
  const showPolicyholder = needsPolicyholder(policyholder.subscriberRelationship)

  return (
    <>
      <div className="border-t border-gray-100 my-5" />
      <div className="flex items-center gap-2 mb-3">
        <Layers size={15} style={{ color: BLUE }} />
        <p className="text-[11px] font-bold uppercase tracking-widest pb-1 inline-block" style={{ color: NAVY, borderBottom: '1.5px solid #15354F' }}>
          {t('cob.secondaryTitle', 'Secondary insurance')}
        </p>
      </div>

      {/* Saved secondary + its benefits */}
      {hasSecondary && !editing && (
        <>
          <div className="rounded-[14px] border-[1.5px] border-[#46A8F9] bg-[#E9F8FF] px-3.5 py-2.5 flex items-center justify-between mb-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-5 h-5 rounded-full bg-[#46A8F9] flex items-center justify-center flex-shrink-0"><Check size={12} className="text-white" /></div>
              <span className="min-w-0">
                <span className="text-[13px] font-semibold block truncate" style={{ color: NAVY }}>{secondary.provider}</span>
                <span className="text-[11px] block truncate" style={{ color: SLATE }}>{t('cob.memberId', 'Member ID')} {secondary.memberId}</span>
              </span>
            </div>
            <button onClick={() => setEditing(true)} className="text-[11px] font-semibold flex-shrink-0" style={{ color: BLUE }}>{t('cob.change', 'Change')}</button>
          </div>
          {secondaryEligibility && (
            <div className="mb-3">
              <EligibilityBadge eligibility={secondaryEligibility} variant="detailed" comprehensive={true} />
              {reuse && (
                <p data-secondary-reuse-line className="text-[11.5px] mt-2 leading-snug" style={{ color: SLATE }}>
                  {t('cob.secondaryReusedLine', secondaryReuseParams(reuse, language === 'es' ? 'es-US' : 'en-US'))}
                </p>
              )}
            </div>
          )}
        </>
      )}

      {/* Add CTA */}
      {!hasSecondary && !editing && (
        <button
          onClick={() => setEditing(true)}
          className="w-full py-2.5 border-[1.5px] border-dashed rounded-[14px] text-[13px] font-semibold flex items-center justify-center gap-2 mb-2"
          style={{ borderColor: 'rgba(70,168,249,0.3)', color: BLUE }}
        >
          <Plus size={15} /> {t('cob.addSecondary', 'Add a secondary policy')}
        </button>
      )}
      {!hasSecondary && !editing && (
        <p className="text-[11px] mb-1" style={{ color: SLATE }}>{t('cob.secondaryBlurb', 'Have two plans? Add the second one so we can coordinate benefits and bill it automatically.')}</p>
      )}

      {/* Editor */}
      {editing && (
        <div className="rounded-[14px] border-[1.5px] border-gray-200 p-4 mb-2" data-secondary-editor>
          {/* ★ C4: scan the second card, front and back */}
          <div className="flex items-center gap-2 mb-2">
            <Scan size={15} style={{ color: BLUE }} />
            <span className="text-[12px] font-semibold" style={{ color: NAVY }}>{t('profileInsurance2.scanYourCard')}</span>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            {['cardFront', 'cardBack'].map((side) => (
              <div key={side}>
                {cards[side] ? (
                  <div className="relative aspect-[1.586/1] bg-white rounded-xl overflow-hidden border border-gray-200">
                    <CardThumb card={cards[side]} resolved={cardUrls[side]} alt={side} />
                    <div className="absolute top-1 left-1 bg-green-500 text-white text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                      <Check size={10} />{side === 'cardFront' ? t('profileInsurance.front') : t('profileInsurance.back')}
                    </div>
                    <button onClick={() => { setCards((prev) => ({ ...prev, [side]: null })); cardPair.clearSide(side) }} className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow"><X size={12} /></button>
                  </div>
                ) : (
                  <button onClick={() => setScannerOpen(side)} className="w-full aspect-[1.586/1] border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-1.5" style={{ background: 'rgba(255,255,255,0.7)' }}>
                    <Scan size={20} className="text-gray-400" />
                    <span className="text-[11px] font-medium text-gray-400">{side === 'cardFront' ? t('profileInsurance.scanFront') : t('profileInsurance.scanBack')}</span>
                  </button>
                )}
              </div>
            ))}
          </div>

          <label className="block text-[12px] mb-1" style={{ color: '#6b7785' }}>{t('cob.carrier', 'Carrier')}</label>
          {carrier.name ? (
            <div className="rounded-[14px] border-[1.5px] border-[#46A8F9] bg-[#E9F8FF] px-3.5 py-2.5 flex items-center justify-between mb-3">
              <span className="text-[13px] font-semibold" style={{ color: NAVY }}>{carrier.name}</span>
              <button onClick={() => setCarrier({ name: '', payerId: '' })} className="text-[11px] font-semibold" style={{ color: BLUE }}>{t('cob.change', 'Change')}</button>
            </div>
          ) : (
            <div className="mb-3">
              <div className="relative mb-2">
                <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('cob.searchCarriers', 'Search carriers')}
                  className="w-full pl-10 pr-10 py-2.5 border-[1.5px] border-gray-200 rounded-[14px] text-[13px] focus:outline-none focus:border-[#46A8F9]" style={{ color: NAVY }} />
                {query && <button onClick={() => setQuery('')} className="absolute right-3.5 top-1/2 -translate-y-1/2"><X size={16} className="text-gray-400" /></button>}
              </div>
              {query.trim().length >= 2 && searching && (
                <div className="flex items-center justify-center py-3 gap-2 text-gray-400"><OrellaSpinner size={16} tone="inherit" /><span className="text-[12px]">{t('cob.searchingPlans', 'Searching plans')}</span></div>
              )}
              <div className="space-y-1.5">
                {!searching && results.map(p => (
                  <button key={p.stediId || p.primaryPayerId} onClick={() => pickCarrier(p)}
                    className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-[14px] border-[1.5px] border-gray-200 active:border-[#46A8F9] active:bg-[#E9F8FF] text-left">
                    <span className="text-[13px] font-medium text-gray-700 truncate">{p.displayName}</span>
                  </button>
                ))}
                {query.trim().length >= 2 && !searching && (
                  <button onClick={() => pickCarrier({ name: query.trim() })}
                    className="w-full py-2.5 border-[1.5px] border-dashed rounded-[14px] text-[13px] font-semibold flex items-center justify-center gap-2"
                    style={{ borderColor: 'rgba(70,168,249,0.3)', color: BLUE }}>+ {t('cob.use', 'Use')} &ldquo;{query.trim()}&rdquo;</button>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-[12px] mb-1" style={{ color: '#6b7785' }}>{t('cob.memberId', 'Member ID')}</label>
              <input type="text" value={memberId} onChange={(e) => setMemberId(e.target.value)} className={inputCls} style={{ color: NAVY }} placeholder={t('cob.memberId', 'Member ID')} />
            </div>
            <div>
              <label className="block text-[12px] mb-1" style={{ color: '#6b7785' }}>{t('cob.groupNumber', 'Group #')}</label>
              <input type="text" value={groupNumber} onChange={(e) => setGroupNumber(e.target.value)} className={inputCls} style={{ color: NAVY }} placeholder={t('cob.optional', 'Optional')} />
            </div>
          </div>

          {/* ★ C4: whose plan the secondary is */}
          <div className="mb-3">
            <label className="block text-[12px] mb-1" style={{ color: '#6b7785' }}>{t('profileInsurance2.subscriberRelationship')}</label>
            <div className="grid grid-cols-3 gap-2">
              {[{ value: 'Self', label: t('profileInsurance2.me', 'Me') }, { value: 'Spouse', label: t('profileInsurance2.spouse') }, { value: 'Parent', label: t('profileInsurance2.parent') }, { value: 'Child', label: t('profileInsurance2.child', 'Child') }, { value: 'Other', label: t('profileInsurance2.other') }].map(opt => {
                const isSel = policyholder.subscriberRelationship === opt.value
                return (
                  <button key={opt.value} onClick={() => setPolicyholder((prev) => ({ ...prev, subscriberRelationship: opt.value }))}
                    className={`py-2 rounded-[14px] text-[12px] font-semibold transition-all border-[1.5px] ${isSel ? 'border-[#46A8F9] bg-[#E9F8FF]' : 'border-gray-200 text-gray-400'}`}
                    style={isSel ? { color: NAVY } : {}}>{opt.label}</button>
                )
              })}
            </div>
          </div>
          {showPolicyholder && (
            <div className="rounded-[14px] border-[1.5px] border-gray-200 p-3 mb-3">
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2">{t('profileInsurance2.subscriberInformation')}</p>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-[12px] text-gray-500 mb-1">{t('profileId.firstName')}</label>
                  <input type="text" value={policyholder.subscriberFirstName} onChange={(e) => setPolicyholder((prev) => ({ ...prev, subscriberFirstName: e.target.value }))} className={inputCls} style={{ color: NAVY }} />
                </div>
                <div>
                  <label className="block text-[12px] text-gray-500 mb-1">{t('profileId.lastName')}</label>
                  <input type="text" value={policyholder.subscriberLastName} onChange={(e) => setPolicyholder((prev) => ({ ...prev, subscriberLastName: e.target.value }))} className={inputCls} style={{ color: NAVY }} />
                </div>
              </div>
              <label className="block text-[12px] text-gray-500 mb-1">{t('profileId.dob')}</label>
              <DobPicker value={policyholder.subscriberDOB} onChange={(v) => setPolicyholder((prev) => ({ ...prev, subscriberDOB: v }))} />
            </div>
          )}

          {/* ★ C4: search for the secondary (its own 3) */}
          {INSURANCE_DISCOVERY_ENABLED && (
          <div className="mb-3" data-insurance-search="secondary">
            <button type="button" onClick={handleSearch} disabled={searchBlocked}
              className="w-full py-2.5 border-[1.5px] rounded-[14px] text-[13px] font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ borderColor: 'rgba(70,168,249,0.45)', color: BLUE, background: '#fff' }}>
              {searchStatus === 'searching' ? <OrellaSpinner size={16} tone="inherit" /> : <Search size={15} />}
              {t('insuranceSearch.searchButton')}
            </button>
            {searchesLeft !== null && (
              <p className="text-[11px] text-gray-400 text-center mt-1.5">{t('insuranceSearch.searchesLeft', { count: Math.max(0, Math.min(DISCOVERY_CAP, searchesLeft)) })}</p>
            )}
            {searchStatus === 'searching' && (
              <p className="text-[12px] text-gray-400 text-center mt-2">{t('cob.searchingPlans', 'Searching plans')}</p>
            )}
            {outcomeMessageKey(searchStatus, 'profile') && (
              <p role="status" data-search-outcome={searchStatus} className="text-[12px] mt-2 leading-relaxed" style={{ color: NAVY }}>{t(outcomeMessageKey(searchStatus, 'profile'))}</p>
            )}
            {searchStatus === 'found' && candidates.length > 0 && (
              <div className="mt-3">
                <InsuranceCandidateList
                  candidates={candidates}
                  variant="inline"
                  singleConfirm={candidates.length === 1}
                  onApplyPrimary={applyCandidate}
                  onDone={() => { setSearchStatus('idle'); setCandidates([]) }}
                  onSkip={() => { setSearchStatus('idle'); setCandidates([]) }}
                />
              </div>
            )}
          </div>
          )}

          <div className="flex gap-2">
            <button onClick={save} disabled={!canSave || saving}
              className="flex-1 h-10 rounded-full text-[13px] font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: BLUE }}>
              {saving ? <><OrellaSpinner size={16} tone="inherit" /> {t('cob.saving', 'Saving')}</> : t('cob.saveSecondary', 'Save secondary')}
            </button>
            {hasSecondary && (
              <button onClick={remove} disabled={saving} className="px-4 h-10 rounded-full text-[13px] font-semibold" style={{ background: '#fff', color: NAVY, border: '1.5px solid #D8E6F0' }}>{t('cob.remove', 'Remove')}</button>
            )}
            <button onClick={() => setEditing(false)} disabled={saving} className="px-4 h-10 rounded-full text-[13px] font-semibold" style={{ background: '#fff', color: SLATE, border: '1.5px solid #E5EAF0' }}>{t('cob.cancel', 'Cancel')}</button>
          </div>
        </div>
      )}

      {scannerOpen && (
        <CardScanner cardType="Insurance" side={scannerOpen === 'cardFront' ? 'front' : 'back'}
          onCapture={(cardData) => handleCardCapture(scannerOpen, cardData)} onCancel={() => setScannerOpen(null)}
          onUpload={async (file) => { const side = scannerOpen; const scanToken = cardPair.beginSide(side); try { const cardData = await processCardFile(file, 'Insurance', side === 'cardFront' ? 'front' : 'back'); handleCardCapture(side, cardData, scanToken) } catch (e) { if (!cardPair.isCurrent(scanToken)) return; alert(e?.message || 'Could not read that image — try another photo.') } }}
          enableOCR={true} />
      )}
    </>
  )
}
